import { useEffect, useRef, useState } from 'react';
import { CancelledError, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmSubscriptionRead, onSubscriptionRequired, subscriptionCapabilities, subscriptionRead, type createAccountApi } from '../../api/client';
import type { Envelope } from '../../api/contract';
import { getAccessDuration, type Subscription } from './model';

export function useSubscription(api: ReturnType<typeof createAccountApi>, enabled: boolean) {
  const client = useQueryClient();
  const [denied, setDenied] = useState(false);
  const [now, setNow] = useState(() => performance.now());
  const readGeneration = useRef(0);
  const capabilities = useQuery({ queryKey: ['subscription-capabilities'], queryFn: () => subscriptionCapabilities(api.epoch), enabled, retry: false });
  const state = useQuery({
    queryKey: ['subscription'], enabled: enabled && capabilities.data === true,
    queryFn: async ({ signal }) => {
      const generation = readGeneration.current;
      const requestedAt = performance.now();
      const snapshot = (await subscriptionRead<Envelope<Subscription>>(api.epoch, signal)).data;
      // A successful write supersedes reads started before it, including any
      // late completion side effects after QueryClient cancellation.
      if (generation !== readGeneration.current) throw new CancelledError({ silent: true });
      confirmSubscriptionRead(api.epoch);
      return { snapshot, requestedAt, checkedAt: performance.now() };
    },
    retry: false, refetchInterval: () => document.visibilityState === 'visible' ? 60_000 : false,
  });
  const remaining = state.data ? getAccessDuration(state.data.snapshot) - Math.max(0, Math.max(now, state.data.checkedAt) - state.data.requestedAt) : 0;
  const allowed = capabilities.data === false || (state.isSuccess && !denied && remaining > 0);
  useEffect(() => {
    if (!state.data || !Number.isFinite(remaining) || remaining <= 0) return;
    const delay = getAccessDuration(state.data.snapshot) - Math.max(0, performance.now() - state.data.requestedAt);
    const timer = window.setTimeout(() => { setNow(performance.now()); void client.invalidateQueries({ queryKey: ['subscription'] }); }, Math.min(Math.max(0, delay) + 1, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [client, remaining, state.data]);
  useEffect(() => {
    const resume = () => { setNow(performance.now()); };
    window.addEventListener('pageshow', resume);
    document.addEventListener('visibilitychange', resume);
    return () => { window.removeEventListener('pageshow', resume); document.removeEventListener('visibilitychange', resume); };
  }, []);
  useEffect(() => onSubscriptionRequired(() => {
    setDenied(true);
    void client.cancelQueries({ predicate: query => !['me', 'subscription', 'subscription-capabilities', 'sessions'].includes(String(query.queryKey[0])) });
    client.removeQueries({ predicate: query => !['me', 'subscription', 'subscription-capabilities', 'sessions'].includes(String(query.queryKey[0])) });
    void client.invalidateQueries({ queryKey: ['subscription'] });
  }), [client]);
  useEffect(() => {
    if (allowed) return;
    void client.cancelQueries({ predicate: query => ['workspaces', 'sync', 'finance', 'cashback', 'dashboard', 'history', 'report', 'members'].includes(String(query.queryKey[0])) });
    client.removeQueries({ predicate: query => ['workspaces', 'sync', 'finance', 'cashback', 'dashboard', 'history', 'report', 'members'].includes(String(query.queryKey[0])) });
  }, [allowed, client]);
  return {
    loading: enabled && (capabilities.isPending || (capabilities.data === true && state.isPending)),
    error: capabilities.error ?? state.error,
    snapshot: state.data?.snapshot,
    allowed,
    refresh: async () => {
      const capabilityResult = await capabilities.refetch();
      api.assertCurrent();
      if (capabilityResult.error) throw capabilityResult.error;
      if (capabilityResult.data) {
        const result = await state.refetch();
        api.assertCurrent();
        if (result.error) throw result.error;
      }
      setDenied(false);
    },
    accept: async (snapshot: Subscription, requestedAt: number) => {
      api.assertCurrent();
      const generation = ++readGeneration.current;
      await client.cancelQueries({ queryKey: ['subscription'], exact: true });
      api.assertCurrent();
      if (generation !== readGeneration.current) return;
      setDenied(false);
      client.setQueryData(['subscription'], { snapshot, requestedAt, checkedAt: performance.now() });
      confirmSubscriptionRead(api.epoch);
    },
  };
}
export type SubscriptionState = ReturnType<typeof useSubscription>;
